/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import DisplayPrice from '../display-price'
import Promotions from './partials/promotions'

// Components
import {
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    HStack,
    Button,
    Image,
    IconButton,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Circle} from '@chakra-ui/react'
import DynamicImage from '../dynamic-image'

// Project Components
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import withRegistration from '../with-registration'
import WishlistAnimation from './partials/wishlist-animation'
import Badges from './partials/badges'
import TextBadges from './partials/text-badges'

// Hooks
import {useIntl, FormattedMessage} from 'react-intl'

// Other
import {PRODUCT_TILE_IMAGE_VIEW_TYPE} from '../../constants'
import {
    formatCategoryTree,
    getPriceData,
    isCharmBadge,
    isCharmProduct,
    selectPlpImageBadges
} from '../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {filterImageGroups, getDecoratedVariationAttributes} from '../../utils/product-utils'
import {
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2
} from '../../constants'
import useTagManager from '../../hooks/use-tag-manager'
import QuickAdd from './partials/quick-add'
import getCloudinaryVideoUrl from '../cloudinary-media/video'
import {getCloudinaryImageUrl} from '../cloudinary-media/image'
import {getUrl} from '../../utils/cloudinary-utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMonetate from '../../hooks/use-monetate'
import useDeferredNavigation from '../../hooks/use-deferred-navigation'
import {formattedURL} from '../../utils/url'
import {ENGAGEMENT_PRODUCTS} from '../../constants'
import {useGlobaleContext} from '../../contexts/globale'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useSiteContext} from '../../contexts/site'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../utils/common-utils'

const IconButtonWithRegistration = withRegistration(IconButton)

// Component Skeleton
const PricingAndPromotionsSkeleton = () => {
    return (
        <Stack
            spacing={2}
            data-testid="sf-product-tile-pricing-and-promotions-skeleton"
            marginBottom="14px"
        >
            <ChakraSkeleton width="80px" height="20px" />
        </Stack>
    )
}

export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTileStyles')
    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <ChakraSkeleton aspectRatio="auto 300 / 375" />
                </Box>
                <Box margin="10px 10px 0 10px">
                    <ChakraSkeleton height="22px" width="92%" marginBottom="10px" />
                    <ChakraSkeleton height="22px" width="70%" marginBottom="10px" />
                    <PricingAndPromotionsSkeleton />
                    <ChakraSkeleton height="20px" width="55%" marginBottom="10px" />
                    <ChakraSkeleton
                        display={{base: 'block', lg: 'none'}}
                        height="34px"
                        width="100%"
                        marginTop="10px"
                    />
                </Box>
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = (props) => {
    const {
        dynamicImageProps,
        sortLabel = 'Trending Now',
        enableFavourite = false,
        imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE,
        onFavouriteToggle,
        product,
        isBloomReachSearch = false,
        category,
        dataLayerLoaded = false,
        wishlist = {},
        quickAddLabel = true,
        wishlistAnimation = false,
        index,
        isplp = false,
        hideVariations = false,
        showImageSlider = true,
        showQuickAdd = true,
        setShowAddtoCartSpinner,
        tilesTitle = 'site search',
        selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1, // Setting this as default.
        selectableAttributeMetal = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
        isRefreshingData = false,
        dataTestid = 'product-tile',
        showBadge = false,
        showTextBadges = false,
        showPromotions = false,
        preventTileClick = false,
        showName = true,
        showPrice = true,
        openQuickAddModal = false,
        analyticsProps = {},
        bonusDiscountLineItemId,
        pwpTargetPromotionId,
        onViewAllClick,
        showMetalLabel = false,
        badgeUrlConfig = {}
    } = props
    if (category) {
        category.id = category.id || category.c_id
        category.name = category.name || category.c_name
        category.parentCategoryTree = category.parentCategoryTree || category.c_parentCategoryTree
    }
    const {imageGroups, representedProduct, variants} = product
    const mobileImagesRef = useRef(null)
    const {isYR, setOpenBonusModal} = useSiteContext()
    const {data: basket} = useCurrentBasket()
    const location = useLocation()
    const intl = useIntl()
    const {currency} = useCurrency()
    const monetate = useMonetate()
    const isFavouriteLoading = useRef(false)
    const {data: customer} = useCurrentCustomer()
    const [showPriceSkeleton, setShowPriceSkeleton] = useState(true)
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    let hasMetal = false

    const styles = useMultiStyleConfig('ProductTileStyles')
    const isEngagementProduct = ENGAGEMENT_PRODUCTS?.some(
        (id) =>
            id ===
            (isBloomReachSearch || !isplp
                ? product?.c_subclassCode?.toLowerCase()
                : product?.representedProduct?.c_subclassCode?.toLowerCase())
    )
    const disableQuickAddByConfig =
        isBloomReachSearch || !isplp
            ? product?.c_enableQuickAdd === false
            : product?.representedProduct?.c_enableQuickAdd === false
    const salePriceColor =
        product?.c_promotions?.filter((promo) => {
            return promo?.textColor
        })?.[0]?.textColor || 'green.500'

    const {countryISO, currencyCode, globaleCurrency, setRefreshPrices} = useGlobaleContext()
    const isMasterVariant = !!variants
    const initialVariationValue =
        isplp && !isBloomReachSearch
            ? isMasterVariant && !!representedProduct
                ? variants?.find((variant) => variant.productId == product.representedProduct.id)
                      ?.variationValues?.[
                      isEngagementProduct ? selectableAttributeMetal : selectableAttributeId
                  ]
                : undefined
            : variants?.find((variant) => variant.productId == product.id)?.variationValues?.[
                  isEngagementProduct ? selectableAttributeMetal : selectableAttributeId
              ] ||
              (!isplp &&
                  variants?.find((variant) => variant?.orderable)?.variationValues?.[
                      isEngagementProduct ? selectableAttributeMetal : selectableAttributeId
                  ]) ||
              variants?.[0]?.variationValues?.[
                  isEngagementProduct ? selectableAttributeMetal : selectableAttributeId
              ] ||
              product?.c_colorMaterial ||
              product?.c_color

    const [selectableAttributeValue, setSelectableAttributeValue] = useState(initialVariationValue)

    useEffect(() => {
        if (initialVariationValue) {
            setSelectableAttributeValue(initialVariationValue)
        }
    }, [initialVariationValue])

    // Primary image for the tile, the image is determined from the product and selected variation attributes.
    const image = useMemo(() => {
        // NOTE: If the selectable variation attribute doesn't exist in the products variation attributes
        // array, lets not filter the image groups on it. This ensures we always return an image for non-variant
        // type products.
        const hasSelectableAttribute = product?.variationAttributes?.find(
            ({id}) => id === selectableAttributeId
        )

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}
        const filteredImageGroups = filterImageGroups(imageGroups, {
            viewType: imageViewType,
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        const defaultImageGroups = filterImageGroups(imageGroups, {
            // Please return the default if there is no variationValues available
            viewType: imageViewType
        })
        // Return the first image of the first group (if there is no group filtered, then return default).
        return filteredImageGroups?.length
            ? filteredImageGroups[0]?.images[0]
            : defaultImageGroups?.[0]?.images[0]
    }, [product, selectableAttributeId, selectableAttributeValue, imageViewType])

    // NOTE: variationAttributes are only defined for master/variant type products.
    const variationAttributes = useMemo(() => getDecoratedVariationAttributes(product), [product])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name ?? product.productName

    const [productWithFilteredVariants, setProductWithFilteredVariants] = useState(() => {
        const variants = product?.variants?.filter(
            ({variationValues, productId}) =>
                variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                productId ===
                    (isBloomReachSearch || !isplp ? product?.id : product?.representedProduct?.id)
        )
        if (variants?.length) {
            return {
                ...product,
                variants
            }
        } else {
            return product
        }
    })
    const [priceData, setPriceData] = useState(getPriceData(productWithFilteredVariants))
    const [shouldFetchPriceData, setShouldFetchPriceData] = useState(false)

    useEffect(() => {
        const fetchVariant = async () => {
            if (!product || !selectableAttributeId || !selectableAttributeValue) return

            let variants = product.variants?.filter(
                ({variationValues, productId}) =>
                    variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                    product?.c_allPdpLinks?.find((p) => p.id === productId) &&
                    productId === product?.id
            )
            if (!variants?.length) {
                variants = [
                    product.variants?.find(
                        ({variationValues, productId}) =>
                            variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                            product?.c_allPdpLinks?.find((p) => p.id === productId)
                    )
                ]
            }

            if (
                ((typeof isBloomReachSearch === 'boolean' && !isBloomReachSearch) || !isplp) &&
                !shouldFetchPriceData
            ) {
                return
            }

            /**
             * Bloomreach search results are Variants from Get Products API
             * SCAPI search results are Master products from Product Search API
             *
             * tieredPrices (used for discounts) are necessary for strikethrough prices
             *
             * We append tiered prices from product.variant.tieredPrices (product variable is from ProductSearch API)
             * Because tieredPrices is missing in Get Products API
             */
            if ((isBloomReachSearch || !isplp) && shouldFetchPriceData) {
                if (!variants.length) {
                    setProductWithFilteredVariants({
                        ...product,
                        variants
                    })

                    return
                }

                const token = await getTokenWhenReady()
                const requestParams = {id: variants[0].productId}

                if (countryISO && currencyCode) {
                    requestParams.c_selectedCountry = countryISO
                    requestParams.c_selectedCurrency = currencyCode
                }
                setShowPriceSkeleton(true)
                try {
                    const variantResponse = await api.shopperProducts.getProduct({
                        parameters: requestParams,
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })

                    setProductWithFilteredVariants({
                        ...variantResponse,
                        tieredPrices: variants[0].tieredPrices
                    })

                    setShouldFetchPriceData(false)
                    setShowPriceSkeleton(false)
                } catch (e) {
                    setShowPriceSkeleton(false)
                }
                return
            }
        }

        fetchVariant()
    }, [
        product,
        selectableAttributeId,
        selectableAttributeValue,
        shouldFetchPriceData,
        isplp,
        isBloomReachSearch
    ])

    /**
     * productWithFilteredVariants is used to fetch prices
     * It needs to be updated when `product` is updated (Global-E product-search results)
     */
    useEffect(() => {
        if (product) {
            setProductWithFilteredVariants(() => {
                let variants = product.variants?.filter(
                    ({variationValues, productId}) =>
                        variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                        productId === product?.representedProduct?.id
                )
                if (!variants?.length) {
                    variants = [
                        product.variants?.find(
                            ({variationValues, productId}) =>
                                variationValues?.[selectableAttributeId] ===
                                    selectableAttributeValue &&
                                product?.c_allPdpLinks?.some((p) => p.id === productId)
                        )
                    ]
                }
                if (variants?.length) {
                    return {
                        ...product,
                        variants
                    }
                } else {
                    return product
                }
            })
        }
    }, [product, selectableAttributeId, selectableAttributeValue])

    useEffect(() => {
        if (productWithFilteredVariants) {
            const price = getPriceData(productWithFilteredVariants)
            setPriceData(price)
        }
    }, [productWithFilteredVariants])

    const [selectedImages, setSelectedImages] = useState([image?.disBaseLink])
    const isCaratProduct = variants?.some((v) => v?.variationValues && 'carat' in v.variationValues)
    useEffect(() => {
        let links = []
        if (isEngagementProduct) {
            links = product.imageGroups
                .filter((g) => g?.viewType === 'large')
                .filter((g) =>
                    g.variationAttributes?.some((a) =>
                        a?.values?.some((v) => v.value === selectableAttributeValue)
                    )
                )
                .map((g) => g.images?.[0]?.disBaseLink)
                .filter(Boolean)
        } else {
            links = product?.imageGroups
                ?.filter(
                    (g) =>
                        g?.viewType === 'large' &&
                        g?.variationAttributes?.some((a) =>
                            a?.values?.some((v) => v.value === selectableAttributeValue)
                        )
                )
                .flatMap((g) => g.images.map((i) => i.disBaseLink))
        }
        // If variationAttributes is not available, then use the default image
        if (!links?.length) {
            links = product?.imageGroups
                ?.filter((g) => g?.viewType === 'large')
                .flatMap((g) => g.images.map((i) => i.disBaseLink))
        }
        if (showImageSlider) {
            setSelectedImages(links)
        } else {
            setSelectedImages([links?.[0]])
        }
    }, [selectableAttributeValue, product?.id, showImageSlider, variants])
    const [currentIndex, setCurrentIndex] = useState(0)
    const handleScroll = (e) => {
        const scrollLeft = e.target.scrollLeft
        const width = e.target.offsetWidth
        const newIndex = Math.round(scrollLeft / width)
        setCurrentIndex(newIndex)
    }

    // A product tile can render several swipe images. Only the first can be the
    // initial LCP candidate; keep the rest out of the critical request queue.
    const {fetchpriority, ...providedImageProps} = dynamicImageProps?.imageProps || {}
    const imageProps = {
        background: 'gray.100',
        widths: dynamicImageProps?.widths,
        imgSizes: '(min-width: 80em) 25vw, (min-width: 62em) 25vw, (min-width: 48em) 25vw, 50vw',
        imageProps: {
            alt: product.name || product.productName,
            loading: providedImageProps.loading || 'lazy',
            height: 'auto',
            aspectRatio: '300/375',
            width: '100%',
            mixBlendMode: 'multiply',
            ...providedImageProps,
            ...(fetchpriority && !providedImageProps.fetchPriority
                ? {fetchPriority: fetchpriority}
                : {})
        }
    }
    const deferredImageProps = {
        ...imageProps,
        imageProps: {
            ...imageProps.imageProps,
            loading: 'lazy',
            fetchPriority: 'auto'
        }
    }
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [isImageHovered, setIsImageHovered] = useState(false)
    const [isTileHovered, setIsTileHovered] = useState(false)
    let selectedVariant = product?.variants?.find(
        ({variationValues, productId}) =>
            productId ===
                (isBloomReachSearch || !isplp ? product.id : product?.representedProduct?.id) &&
            variationValues?.[selectableAttributeId] === selectableAttributeValue
    )
    if (!selectedVariant) {
        selectedVariant = product?.variants?.find(
            ({variationValues, productId}) =>
                variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                product?.c_allPdpLinks?.some((p) => p.id === productId)
        )
    }
    // Primary URL user to wrap the ProduceTile.
    const productUrl = product?.productType?.set
        ? product?.c_url
        : product?.c_allPdpLinks?.find((p) => p.id === selectedVariant?.productId)?.url ||
          product?.slugUrl ||
          product?.c_url
    const isExternalRedirection =
        product?.productType?.set || product?.type?.set || isEngagementProduct
    const productUrlFormatted = formattedURL(productUrl, isExternalRedirection)

    const trackingProps =
        Object.keys(analyticsProps).length > 0
            ? {
                  ...analyticsProps,
                  tile_name: selectedVariant?.name || product?.name || product?.productName,
                  image_url: image?.disBaseLink,
                  id: selectedVariant?.productId || product?.id || product?.productId
              }
            : {}
    // Include componentProps in Link state so PDP can access it for product view tracking
    const productUrlFormattedWithState = {
        ...productUrlFormatted,
        ...(trackingProps ? {state: {trackingProps}} : {})
    }
    const [hideMoreSwatches, setHideMoreSwatches] = useState(true)

    useEffect(() => {
        setHideMoreSwatches(true)
    }, [location?.pathname, location?.search])

    const tagManager = useTagManager()
    const tileRef = useRef(null)
    const deferredNavigate = useDeferredNavigation()

    useEffect(() => {
        isFavouriteLoading.current = false // If user logged in, then refresh the current reference.
    }, [customer?.isRegistered, selectedVariant?.productId])
    const isFavourite = !!wishlist?.customerProductListItems?.find(
        (item) =>
            (product?.productType?.set ? product?.productId : selectedVariant?.productId) ===
            item.productId
    )
    useEffect(() => {
        setTimeout(() => {
            setShowPriceSkeleton(!globaleCurrency || globaleCurrency != product?.c_selectedCurrency)
        }, 300)
    }, [globaleCurrency, product?.c_selectedCurrency])

    const hasVariantPriorityBadge = productWithFilteredVariants?.c_imageBadges?.some((badge) => {
        const badgeValue = badge?.toLowerCase() || ''
        return (
            isCharmBadge(badge) ||
            badgeValue.includes('text-value') ||
            badgeValue.includes('xx-value')
        )
    })
    const badgeProduct = hasVariantPriorityBadge ? productWithFilteredVariants : product
    const disableQuickAdd =
        disableQuickAddByConfig || isCharmProduct(product) || isCharmProduct(badgeProduct)
    // PLP: Charm Type overrides every badge; otherwise text-value takes precedence over xx-value.
    const badgesToRender = useMemo(
        () => selectPlpImageBadges(badgeProduct, category),
        [badgeProduct, category]
    )

    const breadcrumb = isplp
        ? category?.parentCategoryTree
            ? formatCategoryTree(category.parentCategoryTree)
            : ''
        : product?.c_breadcrumb
              ?.map((item) => item?.htmlValue)
              ?.reverse()
              ?.join('/') || ''

    const trackProductClick = () => {
        scheduleIdleAnalytics(() => {
            try {
                window.__lastComponentProps = trackingProps || null
            } catch (error) {
                console.warn(error.message)
            }

            tagManager.productClick(
                tilesTitle,
                product,
                selectedVariant,
                index + 1,
                breadcrumb,
                category,
                tilesTitle,
                isplp ? sortLabel : undefined
            )
        })
    }

    const handleTileNavigation = (event) => {
        if (preventTileClick) {
            event.preventDefault()
            return
        }

        if (event?.target?.closest?.('.sizes-wrapper,button,[role="button"]')) {
            event.preventDefault()
            return
        }

        if (!productUrlFormatted?.to || isExternalRedirection) {
            event.stopPropagation()
            trackProductClick()
            return
        }

        deferredNavigate(
            event,
            {pathname: productUrlFormatted.to, state: {trackingProps}},
            trackProductClick
        )
    }

    return (
        <Box
            {...styles.container}
            ref={tileRef}
            key={'tile-' + index}
            data-is-not-orderable={product?.inventory?.orderable === false}
            className="tile-wrapper"
        >
            <Box
                data-testid={dataTestid}
                className={`${
                    isplp ? 'plp-product-tile' : 'recommender-product-tile'
                } tile-content`}
                display="grid"
                gridTemplateColumns="minmax(0, 1fr)"
                onMouseEnter={() => setIsTileHovered(true)}
                onMouseLeave={() => setIsTileHovered(false)}
            >
                <Box className="tile-box" gridArea="1 / 1">
                    <Box
                        {...productUrlFormattedWithState}
                        {...styles.imageWrapper}
                        className="image-wrapper"
                        onMouseEnter={() =>
                            setIsImageHovered(
                                selectedImages?.length > 1 &&
                                    selectedImages[1]?.indexOf('/videos/') === -1
                            )
                        }
                        onMouseLeave={() => setIsImageHovered(false)}
                        marginBottom="0"
                        display="block"
                        onClick={handleTileNavigation}
                    >
                        <DynamicImage
                            data-testid="product-tile-image"
                            className="product-tile-image"
                            display={{base: 'none', md: 'block', lg: 'block'}}
                            src={
                                (isImageHovered && isDesktop && selectedImages?.length > 1
                                    ? selectedImages[1]
                                    : image?.disBaseLink) || '/noimage.png'
                            }
                            {...imageProps}
                        />
                        <Box {...styles.mobileImages.box} className="tile-images-wrapper">
                            <Box
                                key={selectedImages?.join(',')} // forces re-mount on image set change
                                {...styles.mobileImages.main}
                                {...(isplp ? {} : {overflowX: 'initial'})}
                                onScroll={handleScroll}
                                className="tile-images-box"
                                ref={mobileImagesRef}
                            >
                                {selectedImages?.map((src, index) => (
                                    <Box
                                        className="mobile-image"
                                        key={index}
                                        {...styles.mobileImages.imageWrapper}
                                    >
                                        {src?.indexOf('/videos/') === -1 ? (
                                            <DynamicImage
                                                src={src || '/noimage.png'}
                                                display={!isplp && index > 0 ? 'none' : 'block'}
                                                data-testid="product-tile-image"
                                                {...(index === 0 ? imageProps : deferredImageProps)}
                                                {...styles.mobileImages.asset}
                                            />
                                        ) : (
                                            <video
                                                src={getCloudinaryVideoUrl({
                                                    src: src,
                                                    aspectRatio: '300:375',
                                                    width: 212
                                                })}
                                                display={!isplp && index > 0 ? 'none' : 'block'}
                                                loop
                                                playsInline
                                                autoPlay
                                                muted={true}
                                            />
                                        )}
                                    </Box>
                                ))}
                                {!selectedImages?.length && (
                                    <Box
                                        className="no-mobile-image"
                                        {...styles.mobileImages.imageWrapper}
                                    >
                                        <DynamicImage
                                            src={'/noimage.png'}
                                            data-testid="product-tile-image"
                                            {...imageProps}
                                            {...styles.mobileImages.asset}
                                        />
                                    </Box>
                                )}
                            </Box>
                            {selectedImages?.length > 1 && isplp && (
                                <HStack
                                    {...styles.mobileImages.sliderDots}
                                    className="tile-images-dots-stack"
                                >
                                    {selectedImages?.map((_, index) => (
                                        <Circle
                                            key={index}
                                            size="4px"
                                            className="tile-images-dots-circle"
                                            width={
                                                index === 4 &&
                                                currentIndex < 4 &&
                                                selectedImages?.length > 4
                                                    ? '10px'
                                                    : '4px'
                                            }
                                            display={
                                                selectedImages?.length > 4 &&
                                                currentIndex < 4 &&
                                                index > 4
                                                    ? 'none'
                                                    : 'block'
                                            }
                                            opacity={currentIndex === index ? 1 : 0.25}
                                            bg="black"
                                        />
                                    ))}
                                </HStack>
                            )}
                        </Box>
                        {!isYR &&
                            isplp &&
                            (product?.representedProduct?.c_isYellowRoseProduct ||
                                product?.c_isYellowRoseProduct) && (
                                <Image
                                    className="yellow-rose-brand-icon"
                                    display={{
                                        base: badgesToRender?.length ? 'none' : 'block',
                                        md: 'block'
                                    }}
                                    {...styles.yrBrandIcon}
                                    alt="Yellow Rose"
                                    src={getUrl('UIImages/images/yr-brand-icon.svg')}
                                />
                            )}
                    </Box>
                    {showBadge && (
                        <Badges
                            imageBadges={badgesToRender}
                            product={badgeProduct}
                            badgeUrlConfig={badgeUrlConfig}
                            onNavigate={(event, url) => deferredNavigate(event, url)}
                        />
                    )}
                    {/** NOTE: Always hide wishlist if price skeleton is there **/}
                    {!showPriceSkeleton && enableFavourite && (
                        <Box
                            className="wishlist-wrapper"
                            opacity={{base: 1, lg: 0}}
                            onClick={(e) => {
                                // stop click event from bubbling
                                // to avoid user from clicking the underlying
                                // product while the favourite icon is disabled
                                e.preventDefault()
                            }}
                        >
                            {/* Animated Hearts */}
                            {wishlistAnimation === index && <WishlistAnimation />}

                            <IconButtonWithRegistration
                                data-testid="wishlist-button"
                                onMouseEnter={() => setIsImageHovered(true)}
                                backgroundColor="gray.100"
                                position="absolute"
                                right="12px"
                                top="12px"
                                borderColor="transparent"
                                _hover={{
                                    background: 'gray.100'
                                }}
                                borderRadius="50%"
                                aria-label={
                                    isFavourite
                                        ? intl.formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                  defaultMessage: 'Remove {product} from wishlist'
                                              },
                                              {product: localizedProductName}
                                          )
                                        : intl.formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.add_to_wishlist',
                                                  defaultMessage: 'Add {product} to wishlist'
                                              },
                                              {product: localizedProductName}
                                          )
                                }
                                icon={
                                    isFavourite ? (
                                        <Image
                                            width="15px"
                                            alt="filled heart"
                                            height="15px"
                                            src={getUrl('UIImages/images/fill-heart-icon.svg')}
                                        />
                                    ) : (
                                        <Image
                                            src={getUrl('UIImages/images/empty-heart-icon.svg')}
                                            width="15px"
                                            alt="empty heart"
                                            height="15px"
                                        />
                                    )
                                }
                                productid={
                                    product?.productType?.set
                                        ? product?.productId
                                        : selectedVariant?.productId
                                }
                                productname={
                                    product?.productType?.set
                                        ? product?.productName
                                        : selectedVariant?.name
                                }
                                category={category?.name || ''}
                                isplp={isplp}
                                wishlistitems={wishlist?.customerProductListItems?.length || 0}
                                onClick={async () => {
                                    if (!isFavouriteLoading.current) {
                                        isFavouriteLoading.current = true

                                        const productId = product?.productType?.set
                                            ? product?.productId
                                            : selectedVariant?.productId

                                        const productName = product?.productType?.set
                                            ? product?.productName
                                            : selectedVariant?.name

                                        // on plp we trigger the events in product-list/index.jsx
                                        if (!isplp) {
                                            const eventName = isFavourite
                                                ? 'remove_from_wishlist'
                                                : 'add_to_wishlist'
                                            tagManager.trackGA4Event({
                                                eventName,
                                                itemsAdded: [{product: product}],
                                                variant: {pid: productId},
                                                method: 'homepage',
                                                currency: currencyCode,
                                                listName: 'homepage',
                                                listId: 'homepage'
                                            })
                                        }

                                        await onFavouriteToggle(
                                            !isFavourite,
                                            productId,
                                            productName
                                        )

                                        isFavouriteLoading.current = false
                                    }
                                }}
                                minWidth="31px"
                                height="31px"
                            />
                        </Box>
                    )}
                </Box>
                <Box margin="10px 10px 0 10px" className="info-wrapper" gridArea="2 / 1">
                    {/* Title */}
                    {showName && (
                        <Box
                            {...productUrlFormattedWithState}
                            className="product-title"
                            _hover={{textDecoration: 'none'}}
                            onClick={handleTileNavigation}
                        >
                            <Text {...styles.title} className="product-name">
                                {selectedVariant?.name || localizedProductName}
                            </Text>
                        </Box>
                    )}

                    {/* Price */}
                    {isRefreshingData || countryISO === null || showPriceSkeleton ? (
                        <PricingAndPromotionsSkeleton />
                    ) : (
                        showPrice && (
                            <Box
                                display="flex"
                                alignItems="baseline"
                                className="tile-price-box"
                                flexWrap="wrap"
                                marginBottom="10px"
                                gap="10px"
                            >
                                {/* Price */}
                                <Box
                                    className="display-price-wrapper"
                                    marginRight="5px"
                                    display={priceData?.currentPrice ? 'block' : 'none'}
                                >
                                    <DisplayPrice
                                        priceData={priceData}
                                        currency={product?.c_selectedCurrency || currency}
                                        symbol={product?.c_selectedCurrencySymbol}
                                        salePriceColor={salePriceColor}
                                    />
                                </Box>

                                {showPromotions && (
                                    <Promotions
                                        product={product}
                                        dataLayerLoaded={dataLayerLoaded}
                                        isBloomReachSearch={isBloomReachSearch}
                                        isplp={isplp}
                                        selectedVariant={selectedVariant || product?.variants?.[0]}
                                    />
                                )}
                            </Box>
                        )
                    )}

                    {showTextBadges && <TextBadges product={product} />}

                    {/* Swatches */}
                    {!hideVariations &&
                        variationAttributes
                            ?.filter(({id}) => selectableAttributeId === id)
                            ?.map(({id, name, values}) => {
                                hasMetal =
                                    (!hasMetal && id == 'metal') || (hasMetal && id != 'metal')
                                        ? true
                                        : false
                                const filteredSwatches = values
                                    ?.filter(({value}) => value !== null)
                                    ?.filter(({value}) => {
                                        const related = product?.c_allPdpLinks.filter(
                                            (link) => link.value === value
                                        )
                                        return related.some(
                                            (link) =>
                                                link?.searchableIfUnavailableFlag ||
                                                link?.comingSoonEligible ||
                                                link?.orderable ||
                                                !isBloomReachSearch // If Bloomreach is disabled, we'll display data from product-search API.
                                        )
                                    })

                                return (
                                    <SwatchGroup
                                        ariaLabel={name}
                                        key={id}
                                        flexDirection="row"
                                        display="flex"
                                        value={selectableAttributeValue}
                                        groupProps={{gap: 1}}
                                        handleChange={(value, isClick, colorName) => {
                                            if (!value && !isClick && !colorName) {
                                                return
                                            }
                                            if (isClick && (isBloomReachSearch || !isplp)) {
                                                setShouldFetchPriceData(true)
                                            }

                                            if (typeof value === 'string') {
                                                setSelectableAttributeValue(value)
                                                scheduleAfterPaint(() => {
                                                    if (mobileImagesRef?.current) {
                                                        mobileImagesRef.current.scrollLeft = 0
                                                    }
                                                })
                                                if (isClick && isplp) {
                                                    scheduleIdleAnalytics(() => {
                                                        monetate.setPLPSwatch(colorName)
                                                        const currentSelection =
                                                            localStorage.getItem(id)
                                                        if (currentSelection) {
                                                            localStorage.setItem(
                                                                id,
                                                                currentSelection + ',' + colorName
                                                            )
                                                        } else {
                                                            localStorage.setItem(id, colorName)
                                                        }
                                                    })
                                                }
                                            } else if (onViewAllClick) {
                                                const btn = Array.from(
                                                    tileRef.current?.querySelectorAll(
                                                        '.quick-add-button'
                                                    ) || []
                                                ).find((b) => b.offsetParent !== null)
                                                if (btn) btn.click()
                                            } else {
                                                setHideMoreSwatches(false)
                                            }
                                        }}
                                        label={
                                            hasMetal
                                                ? intl.formatMessage(
                                                      {
                                                          defaultMessage: 'Select a {groupname}',
                                                          id: 'product_view.label.group_variant_type'
                                                      },
                                                      {groupname: name}
                                                  )
                                                : ''
                                        }
                                        showLabel={hasMetal && !isplp && showMetalLabel}
                                        isRecommendation={true}
                                    >
                                        {filteredSwatches
                                            ?.sort((a, b) => {
                                                if (id === 'letter') {
                                                    return a?.name?.localeCompare(
                                                        b.name,
                                                        undefined,
                                                        {
                                                            sensitivity: 'base'
                                                        }
                                                    )
                                                }
                                                if (a.value === initialVariationValue) return -1
                                                if (b.value === initialVariationValue) return 1
                                                return 0
                                            })
                                            ?.map(({name, swatch, value}, index) => {
                                                const content = swatch ? (
                                                    <Box
                                                        display={
                                                            index > 3 && hideMoreSwatches
                                                                ? 'none'
                                                                : 'block'
                                                        }
                                                        height="100%"
                                                        width="100%"
                                                        className="swatch-box"
                                                        backgroundRepeat="no-repeat"
                                                        backgroundSize="cover"
                                                        data-id={id}
                                                        backgroundColor={name.toLowerCase()}
                                                        style={{
                                                            backgroundImage: `url('${
                                                                getCloudinaryImageUrl({
                                                                    src: swatch.disBaseLink,
                                                                    width: 20,
                                                                    height: 20
                                                                }) ||
                                                                getCloudinaryImageUrl({
                                                                    src: swatch.link,
                                                                    width: 20,
                                                                    height: 20
                                                                })
                                                            }')`
                                                        }}
                                                    />
                                                ) : (
                                                    name
                                                )
                                                const isOrderable = variants.some(
                                                    (v) =>
                                                        v?.orderable &&
                                                        v?.variationValues?.[
                                                            selectableAttributeId
                                                        ] === value
                                                )
                                                return (
                                                    <Swatch
                                                        swatchProps={{
                                                            display:
                                                                index > 2 && hideMoreSwatches
                                                                    ? 'none'
                                                                    : 'block'
                                                        }}
                                                        key={value}
                                                        ispdp={false}
                                                        isProductPartOfSet={false}
                                                        value={value}
                                                        name={name}
                                                        disabled={!isOrderable}
                                                        variant={'circle'}
                                                    >
                                                        {content}
                                                    </Swatch>
                                                )
                                            })}
                                        {filteredSwatches?.length > 3 && hideMoreSwatches && (
                                            <Button
                                                variant="unstyled"
                                                height="30px"
                                                width="auto"
                                                lineHeight="16px"
                                                className="swatch-more"
                                                border="none"
                                                padding="0"
                                                minHeight="initial"
                                                minWidth="initial"
                                                textDecoration="none"
                                                _hover={{textDecoration: 'none'}}
                                            >
                                                <Text
                                                    as="span"
                                                    color="black.100"
                                                    className="swatch-more-text"
                                                    fontSize="13px"
                                                    fontWeight="300"
                                                >
                                                    {onViewAllClick ? (
                                                        <FormattedMessage
                                                            defaultMessage="View All {count} Variants"
                                                            values={{
                                                                count: filteredSwatches.length
                                                            }}
                                                            id="product_tile.button.view_all_variants"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            defaultMessage="+ {count} more"
                                                            values={{
                                                                count: filteredSwatches.length - 3
                                                            }}
                                                            id="product_tile.button.more_swatches"
                                                        />
                                                    )}
                                                </Text>
                                            </Button>
                                        )}
                                    </SwatchGroup>
                                )
                            })}
                </Box>
                {!disableQuickAdd &&
                    showQuickAdd &&
                    (selectedVariant?.productId || product?.variants?.[0]?.productId) &&
                    !isCaratProduct &&
                    !isEngagementProduct && (
                        <Box
                            display="block"
                            className="quick-add-parent"
                            gridArea={{base: '3 / 1', lg: quickAddLabel ? '1 / 1' : '3 / 1'}}
                            position="relative"
                            zIndex={{base: 'auto', lg: quickAddLabel ? 1 : 'auto'}}
                            pointerEvents={{base: 'auto', lg: quickAddLabel ? 'none' : 'auto'}}
                            height={!quickAddLabel ? '32px' : 'auto'}
                            marginTop={{base: '10px', lg: 0}}
                            marginInline={{base: '10px', lg: quickAddLabel ? 0 : '10px'}}
                        >
                            <QuickAdd
                                isHovered={isTileHovered}
                                isplp={isplp}
                                category={category}
                                tilesTitle={tilesTitle}
                                quickAddLabel={quickAddLabel}
                                basket={basket}
                                wishlist={wishlist}
                                index={index}
                                setShowAddtoCartSpinner={setShowAddtoCartSpinner}
                                productId={
                                    selectedVariant?.productId || product?.variants?.[0]?.productId
                                }
                                variationValues={
                                    selectedVariant?.variationValues ||
                                    product?.variants?.[0]?.variationValues
                                }
                                variants={variants}
                                tileProduct={product}
                                selectedImage={image?.disBaseLink}
                                selectedVariant={selectedVariant || product?.variants?.[0]}
                                analyticsProps={trackingProps}
                                openQuickAddModal={openQuickAddModal}
                                bonusDiscountLineItemId={bonusDiscountLineItemId}
                                pwpTargetPromotionId={pwpTargetPromotionId}
                                customer={customer}
                                tagManager={tagManager}
                                monetate={monetate}
                                formatMessage={intl.formatMessage}
                                countryISO={countryISO}
                                currencyCode={currencyCode}
                                globaleCurrency={globaleCurrency}
                                setRefreshPrices={setRefreshPrices}
                                setOpenBonusModal={setOpenBonusModal}
                                pathname={location.pathname}
                                isMobile={!isDesktop}
                            />
                        </Box>
                    )}
            </Box>
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.any,
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    wishlist: PropTypes.object,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * The `viewType` of the image component. This defaults to 'large'.
     */
    imageViewType: PropTypes.string,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    selectableAttributeMetal: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    category: PropTypes.object,
    tilesTitle: PropTypes.string,
    showQuickAdd: PropTypes.bool,
    quickAddLabel: PropTypes.bool,
    wishlistAnimation: PropTypes.any,
    selectedCurrency: PropTypes.string,
    setShowAddtoCartSpinner: PropTypes.func,
    index: PropTypes.any,
    /**
     * Determines whether to display a skeleton over personalizable data (e.g., pricing and promotions) during data refresh.
     */
    isRefreshingData: PropTypes.bool,
    /**
     * Determines if user is on the PLP tile or not.
     */
    isplp: PropTypes.bool,
    /**
     * Boolean attribute used to hide/show the variations on the tile
     */
    hideVariations: PropTypes.bool,
    /**
     * Boolean attribute used to hide/show the image slider on the tile
     */
    showImageSlider: PropTypes.bool,
    dataTestid: PropTypes.string,
    /**
     * Boolean attribute used to hide/show the badges on the tile
     */
    showBadge: PropTypes.bool,
    /**
     * Boolean attribute used to hide/show the promotions on the tile
     */
    showPromotions: PropTypes.bool,
    showName: PropTypes.bool,
    showPrice: PropTypes.bool,
    dataLayerLoaded: PropTypes.bool,
    showTextBadges: PropTypes.bool,
    sortLabel: PropTypes.string,
    isBloomReachSearch: PropTypes.bool,
    preventTileClick: PropTypes.bool,
    openQuickAddModal: PropTypes.bool,
    componentProps: PropTypes.object,
    badgeUrlConfig: PropTypes.objectOf(PropTypes.string),
    analyticsProps: PropTypes.object,
    bonusDiscountLineItemId: PropTypes.string,
    onViewAllClick: PropTypes.bool,
    showMetalLabel: PropTypes.bool,
    pwpTargetPromotionId: PropTypes.string
}

const areProductTilePropsEqual = (prevProps, nextProps) => {
    return (
        prevProps.product === nextProps.product &&
        prevProps.category === nextProps.category &&
        prevProps.wishlist === nextProps.wishlist &&
        prevProps.index === nextProps.index &&
        prevProps.dataLayerLoaded === nextProps.dataLayerLoaded &&
        prevProps.isRefreshingData === nextProps.isRefreshingData &&
        prevProps.wishlistAnimation === nextProps.wishlistAnimation &&
        prevProps.selectedCurrency === nextProps.selectedCurrency &&
        prevProps.selectedCountry === nextProps.selectedCountry &&
        prevProps.sortLabel === nextProps.sortLabel &&
        prevProps.isBloomReachSearch === nextProps.isBloomReachSearch &&
        prevProps.preventTileClick === nextProps.preventTileClick &&
        prevProps.imageViewType === nextProps.imageViewType &&
        prevProps.selectableAttributeId === nextProps.selectableAttributeId &&
        prevProps.selectableAttributeMetal === nextProps.selectableAttributeMetal &&
        prevProps.enableFavourite === nextProps.enableFavourite &&
        prevProps.isplp === nextProps.isplp &&
        prevProps.hideVariations === nextProps.hideVariations &&
        prevProps.showImageSlider === nextProps.showImageSlider &&
        prevProps.showQuickAdd === nextProps.showQuickAdd &&
        prevProps.quickAddLabel === nextProps.quickAddLabel &&
        prevProps.showBadge === nextProps.showBadge &&
        prevProps.badgeUrlConfig === nextProps.badgeUrlConfig &&
        prevProps.showTextBadges === nextProps.showTextBadges &&
        prevProps.showPromotions === nextProps.showPromotions &&
        prevProps.showName === nextProps.showName &&
        prevProps.showPrice === nextProps.showPrice &&
        prevProps.tilesTitle === nextProps.tilesTitle &&
        prevProps.analyticsProps === nextProps.analyticsProps
    )
}

export default React.memo(ProductTile, areProductTilePropsEqual)
